import { action, computed, makeObservable, observable } from "mobx";
import RootStore from "store/rootStore";
import constants from "tools/constants";
import { BATCH_STATUS } from "pages/batches/components/FilteringBatchesDialog";

const SEPARATOR = "||";
class LabelerSegmentationStore {
  @observable images: Array<any> = [];
  @observable originalImages: Array<any> = [];
  @observable isLoading: boolean = false;
  @observable isLoadingSave: boolean = false;
  @observable isError: boolean = false;
  @observable errorMessage: string = "";
  @observable tags: Array<any> = [];
  @observable motifs: Array<any> = [];
  @observable numImage: number = 0;
  @observable batch: any = {};
  @observable dimensions: any = { height: 576, width: 720 };

  constructor(private store: RootStore) {
    makeObservable(this);
  }

  @action fetchImages = async (appFetch: any, batchId: any) => {
    this.setIsLoading(true);
    try {
      this.setIsError(false);
      const images = await appFetch(
        `/api/images/batches/${batchId}/all?offset=0&size=1000`
      );
      this.setImages(images);
      this.setOriginalImages(images);
      this.setIsLoading(false);
    } catch (e) {
      this.setIsError(true);
      this.setIsLoading(false);
      // @ts-ignore
      this.setErrorMessage(e.message);
    }
  };

  @action fetchBatch = async (appFetch: any, batchId: any) => {
    this.setIsLoading(true);
    try {
      this.setIsError(false);
      const batch = await appFetch(
        `${constants.paths.BATCHES_PATH}/${batchId}`
      );
      this.setBatch(batch);
      this.setMotifs(this._buildMotifs(batch));
      this.setDimensions({ height: batch.maxHeight, width: batch.maxWidth });
      this.setIsLoading(false);
    } catch (e) {
      this.setIsError(true);
      this.setIsLoading(false);
      // @ts-ignore
      this.setErrorMessage(e.message);
    }
  };

  _buildMotifs = (batch: any) => {
    const labelingTypes = batch.project.labelingTypes[0];
    // return labelingTypes.reasons || ["ERROR"]
    return labelingTypes.reasons || ["UNREADABLE", "CROPPED", "OCCLUSION"];
  };

  @action buildTags = () => {
    const labelingTypes = this.batch.project.labelingTypes[0];
    const tagsWithColors = labelingTypes.listTagsDto.map(
      (tag: any, index: number) => ({
        ...constants.colors[index],
        legend: tag.name,
        visible: true,
      })
    );
    const newTags = tagsWithColors.map((tag: any) => ({
      ...tag,
      occurences: this.store.labelerSegmentationUiStore.polygons.filter(
        (p: any) => p.labelValue.split(SEPARATOR)[0] === tag.legend && !p.deleted
        // && p.done
      ).length,
    }));
    this.setTags(newTags);
  };

  @computed get currentTag() {
    return this.tags.filter(
      (tag: any, index: number) =>
        index === this.store.labelerSegmentationUiStore.tagSelectedIndex
    )[0];
  }

  buildImageTosave = (polygonLabels: any, image: any) => {
    const { motif, labels } = image;
    return {
      ...image,
      motif,
      labels: {
        ...labels,
        polygonLabels: motif !== null ? [] : polygonLabels,
      },
      lastLabeledBy: null,
    };
  };

  buildPolygonLabels = () => {
    const { scaling, polygons } = this.store.labelerSegmentationUiStore;

    const { x, y } = scaling;
    const polygonLabels = polygons
      .filter((p) => !p.deleted)
      .map((p: any) => ({
        allX: p.points.map((p: any) => p.x / x),
        allY: p.points.map((p: any) => p.y / y),
        labelValue: p.labelValue.split(SEPARATOR)[0],
      }));
    return polygonLabels;
  };

  buildImageBody = (image: any) => {
    const { id, labels, motif, lastLabeledBy } = image;
    return {
      id,
      labels,
      motif,
      lastLabeledBy, // null si l'on veut que le backend remplace par username
    };
  };

  @action saveLabelisations = async (
    appFetch: any,
    userLogin?: string | null
  ) => {
    if (this.isReadOnly) return;
    this.setIsLoadingSave(true);
    this.setIsError(false);
    try {
      const polygonLabels = this.buildPolygonLabels();
      const currentImage = this.images[this.numImage];
      if (polygonLabels.length > 0 || currentImage.motif !== null) {
        const imageTosave = this.buildImageTosave(polygonLabels, currentImage);
        const body = this.buildImageBody(imageTosave);

        const resp = await appFetch(constants.paths.IMAGES_PATH, {
          method: "PUT",
          body: JSON.stringify(body),
        });

        if (resp) {
          this.setImages(
            this.images.map((img: any) =>
              img.id === imageTosave.id
                ? {
                    ...img,
                    ...imageTosave,
                    lastLabeledBy: userLogin || img.lastLabeledBy,
                  }
                : img
            )
          );
        }

        // throw ({ message: "Erreur lors de la sauvegarde" });
        return resp;
      }
    } catch (e) {
      console.log("error ", e);
      // @ts-ignore
      this.setErrorMessage(e.message);
      this.setIsError(true);
    } finally {
      this.setIsLoadingSave(false);
    }
  };

  canNavigateToNextImage = () => {
    const isLastImage = this.numImage == this.images.length - 1;
    // return this.numImage < this.labeledImages.length && !isLastImage
    return !isLastImage;
  };

  @computed get isReadOnly() {
    return (
      this.numImage > this.labeledImages.length ||
      this.batch?.state === BATCH_STATUS.VERIFIED.name
    );
  }

  canNvigateToPrevImage = () => {
    return Number(this.numImage) > 0;
  };

  @computed get labeledImages() {
    return this.images.filter(
      (img: any) => img.labels.polygonLabels.length > 0 || img.motif !== null
    );
  }

  @computed get isImageOutOfIndex() {
    return this.numImage >= this.images.length;
  }

  @action changeMotif = (motifSkip: string | null, clear: boolean = true) => {
    if (this.isReadOnly) return;
    this.store.labelerSegmentationUiStore.setOpenSkipDialog(false);
    this.storeMotif(motifSkip, clear);
  };

  @action storeMotif = (motif: string | null, clearLabels: boolean = true) => {
    this.setImages(
      this.images.map((img: any, index: number) =>
        index === Number(this.numImage)
          ? {
              ...img,
              labels: !clearLabels
                ? img.labels
                : {
                    rectangleLabels: [],
                    polygonLabels: [],
                    simpleLabels: [],
                    country: null,
                  },
              motif,
              lastLabeledBy: null, // null si l'on veut que le backend remplace par username
            }
          : img
      )
    );
  };

  @action deleteSegmentByName = (name: string) => {
    this.store.labelerSegmentationUiStore.deletePolygonByName(name);
    this.store.fabricSegmentationStore.clearCanvasObjectsByName(name);
  };

  @action deleteAllSegments = () => {
    this.store.fabricSegmentationStore.clearAllCanvasObjects();
    this.store.labelerSegmentationUiStore.setPolygons([]);
    this.store.stickTwoPolygonsStore.resetPolyStick();
  };

  @action restore = () => {
    const {
      setPolygons,
      originalPolygons,
    } = this.store.labelerSegmentationUiStore;
    this.deleteAllSegments();
    setPolygons(originalPolygons);
    this.setImages(
      this.images.map((img: any, index: number) =>
        index === Number(this.numImage)
          ? this.originalImages[Number(this.numImage)]
          : img
      )
    );
  };

  //// SETTERS /////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////

  @action setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action setIsLoadingSave = (isLoadingSave: boolean) => {
    this.isLoadingSave = isLoadingSave;
  };

  @action setIsError = (isError: boolean) => {
    this.isError = isError;
  };

  @action setErrorMessage = (errorMessage: string) => {
    this.errorMessage = errorMessage;
  };

  @action setImages = (images: Array<any>) => {
    this.images = images;
  };

  @action setOriginalImages = (originalImages: Array<any>) => {
    this.originalImages = originalImages;
  };

  @action setTags = (tags: Array<any>) => {
    this.tags = tags;
  };

  @action setMotifs = (motifs: Array<any>) => {
    this.motifs = motifs;
  };

  @action setDimensions = (dimensions: any) => {
    this.dimensions = dimensions;
  };

  @action setBatch = (batch: any) => {
    this.batch = batch;
  };

  @action setNumImage = (numImage: number) => {
    this.numImage = numImage;
  };
}

export default LabelerSegmentationStore;
